import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';
import { queryClient } from '@/queryClient';
import offlineStorage from '@/services/pos/offlineStorage';
import { LoginRequest, LoginResponse, RegisterBusinessRequest, User } from '@saas-platform/shared-types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<{ twoFactorRequired?: boolean } | void>;
  verifyTwoFactor: (payload: { code: string; isBackupCode?: boolean; trustDevice?: boolean }) => Promise<void>;
  register: (userData: RegisterBusinessRequest) => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      const session = await apiService.getSession();
      setIsAuthenticated(true);
      setUser(session.user);
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const login = async (credentials: LoginRequest): Promise<{ twoFactorRequired?: boolean } | void> => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiService.login(credentials);
      if (response.twoFactorRequired) {
        return { twoFactorRequired: true };
      }
      queryClient.clear();
      setUser(response.user);
      setIsAuthenticated(true);

      // Navigate to dashboard after successful login
      navigate('/home');
    } catch (error: any) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async (payload: {
    code: string;
    isBackupCode?: boolean;
    trustDevice?: boolean;
  }): Promise<void> => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiService.verifyTwoFactorLogin(payload);
      queryClient.clear();
      setUser(response.user);
      setIsAuthenticated(true);
      navigate('/home');
    } catch (error: any) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterBusinessRequest): Promise<void> => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiService.register(userData);
      queryClient.clear();
      setUser(response.user);
      setIsAuthenticated(true);

      // Navigate to dashboard after successful registration
      navigate('/home');
    } catch (error: any) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (email: string, code: string): Promise<void> => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiService.verifyEmail(email, code);
      queryClient.clear();
      setUser(response.user);
      setIsAuthenticated(true);
      navigate('/home');
    } catch (error: any) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    // Clear local state immediately for instant logout
    queryClient.clear();
    void offlineStorage.clearAll().catch(() => {});
    if (typeof caches !== 'undefined') {
      void caches.delete('products-cache').catch(() => {});
    }
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');

    // Fire server logout in background — best-effort
    try {
      await apiService.logout();
    } catch {
      // Silently ignore — user is already logged out locally
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      await apiService.refreshToken();
      // Re-check auth status after token refresh
      await checkAuth();
    } catch (error) {
      // If refresh fails, logout
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    verifyTwoFactor,
    register,
    verifyEmail,
    logout,
    refreshToken,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
